



















































































import { defineComponent } from "@vue/composition-api";
import { Location } from "vue-router";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { getElement } from "@/lib/elementHelpers";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import { isWebRTCSupported, isGetUserMediaSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import { BaseMenuItem, BlockingStatus } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseList from "@/components/BaseList";
import { BaseListItem } from "@/components/BaseList";
import BaseMenu from "@/components/BaseMenu";
import BasePage from "@/components/BasePage";
import BlockUserController from "@/components/BlockUserController";
import MessageSendWithAttachment from "@/components/MessageSendWithAttachment";
import PageTitle from "@/components/PageTitle";
import MessageDeleteConfirm from "@/views/PageMessages/components/MessageDeleteConfirm.vue";
import GroupModal from "./GroupModal.vue";
import MessageList from "./MessageList.vue";
import MessageSystemNote from "./MessageSystemNote.vue";

export default defineComponent({
  name: "MessagesView",
  components: {
    MessageDeleteConfirm,
    MessageList,
    BasePage,
    PageTitle,
    BaseAvatar,
    MessageSendWithAttachment,
    BaseIcon,
    BlockUserController,
    BaseIconButton,
    BaseList,
    BaseListItem,
    BaseMenu,
    MessageSystemNote,
    GroupModal,
  },
  props: {
    loading: { type: Boolean, required: true },
    allowResponse: { type: Boolean, required: true },
    authUserId: { type: String, required: true },
    messageThread: { type: Object as () => MessageThread | null },
    messages: { type: Array as () => Message[], required: true },
    minUnreadMessageId: { type: String, required: true },
    enableScroll: { type: Boolean, default: false },
    user: { type: Object as () => UserPublicData | null },
    userCallOptions: { type: Object as () => CallOptions | null },
    paymentPackages: { type: Array as () => PaymentPackage[] | null },
  },
  setup() {
    const { authUser, authorities } = useAuthUser();
    return {
      authUser,
      authorities,
    };
  },
  data() {
    return {
      blockingStatus: {
        authUser: false,
        opponent: false,
      } as BlockingStatus,
      blockingConfirm: false,
      showGroupModal: false,
      messageToDelete: null as Message | null,
    };
  },
  computed: {
    messagesListElement(): Element {
      return getElement(this.$refs.list);
    },
    screenName(): string {
      return this.user?.screenName ?? "";
    },
    pinned(): boolean {
      return !!this.messageThread?.pinTime;
    },
    titleComponent(): string {
      return this.user ? "router-link" : "div";
    },
    profileTo(): Location | undefined {
      return this.user ? getProfileUrl(this.user) : undefined;
    },
    isCallable(): boolean | null | undefined {
      return config.featureCalling && this.user && this.user.id !== this.authUserId && this.userCallOptions?.enabled;
    },
    isWebRTCSupported(): boolean {
      return isWebRTCSupported() && isGetUserMediaSupported();
    },
    userId(): string {
      return this.user?.id ?? "";
    },
    isMessageSendShow(): boolean | null | undefined {
      return this.user && !this.isChatBlocked && this.allowResponse;
    },
    threadId(): string | undefined {
      return this.messageThread ? this.messageThread.id : undefined;
    },
    isCreator(): boolean {
      return checkRole(this.authUser?.role || "", "creator");
    },
    allowManageLists(): boolean {
      return checkAuthorities(this.authorities, ["manageMyGroups"]);
    },
    menuItems(): BaseMenuItem[] {
      const menuItems = [];
      if (this.messageThread && !this.isChatBlocked) {
        menuItems.push({
          name: "pin-user",
          text: !this.pinned ? $t("Mark as important") : $t("Unmark as important"),
          prependIcon: "sci-star",
          ...(this.pinned
            ? {
                prependIconColor: "var(--palette-primary)",
                class: "__active",
              }
            : {}),
        });
        if (this.isCreator) {
          if (this.allowManageLists) {
            menuItems.push({
              name: "add-to-list",
              class: "messages__list-icon __add-to-group",
              text: $t("Add to list"),
              prependIcon: "sci-plus",
            });
          }
          menuItems.push({
            name: "block-user",
            text: $t(this.blockingStatus.opponent ? $t("Unblock user") : $t("Block user")),
            prependIcon: "sci-blocked",
            prependIconColor: "var(--palette-negative)",
          });
        }
      }

      return menuItems;
    },
    noteText(): string {
      return this.blockingStatus.opponent
        ? $t("You blocked {0}.", [this.user?.screenName])
        : $t("{0} blocked you. You can’t send messages to this creator anymore.", [this.user?.screenName]);
    },
    isChatBlocked(): boolean {
      return this.blockingStatus.opponent || this.blockingStatus.authUser;
    },
  },
  methods: {
    handleCall() {
      if (this.isWebRTCSupported) {
        emitter.emit(EVENTS.CALL_OUTGOING, { opponentUser: this.user });
      } else {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("This browser does not support WebRTC") });
      }
    },
    addMessage(message: Message) {
      this.$emit("addMessage", message);
      this.messagesListElement.scrollTo({ top: 0 });
    },
    receivedMessage() {
      if (this.messagesListElement.scrollTop < 100) {
        this.messagesListElement.scrollTo({ top: 0 });
      }
    },
    closeDeleteConfirm() {
      this.messageToDelete = null;
    },
    handleDeleted() {
      if (this.messageToDelete) {
        this.$emit("deleted", this.messageToDelete.id);
        this.closeDeleteConfirm();
      }
    },
    reverseScroll(e: WheelEvent) {
      e.preventDefault();
      this.messagesListElement.scrollBy({ top: -e.deltaY });
    },
    toggleShowGroupModal() {
      this.showGroupModal = !this.showGroupModal;
    },
  },
  created() {
    this.$on("receivedMessage", this.receivedMessage);
  },
  beforeDestroy() {
    this.$off("receivedMessage", this.receivedMessage);
  },
});
